import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Link from '@cloudscape-design/components/link';
import RadioGroup from '@cloudscape-design/components/radio-group';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import Toggle from '@cloudscape-design/components/toggle';
import {
  ALL_BR_TAX_REGIMES,
  ALL_CURRENCIES,
  ALL_SELLER_ENTITY_STATUSES,
  MARKET_SELECTABLE_PROVIDERS,
  PaymentMode,
  type Currency,
} from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { CurrencyInput } from '@/components/CurrencyInput';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import {
  adminGetAsaasFiscalCatalogCallable,
  adminGetAsaasFiscalInfoCallable,
  adminSyncAsaasFiscalInfoCallable,
  adminUpsertSellerEntityCallable,
  type AsaasFiscalCatalog,
  type AsaasFiscalStatus,
} from '@/lib/callables';
import { subscribeSellerEntities } from '@/lib/sellerEntities';
import { BR_ENTITY_CNAES } from './cnaeCatalog';
import {
  emptyForm,
  fieldIssues,
  formFromEntity,
  inputFromForm,
  mapsUrl,
  formatTaxIdInput,
  taxIdError,
  type NfseForm,
  type SellerEntityForm,
} from './sellerEntityForm';

type FieldKey = {
  [K in keyof SellerEntityForm]: SellerEntityForm[K] extends string ? K : never;
}[keyof SellerEntityForm];

/**
 * Editor de uma entidade vendedora: dados da empresa, endereço, dados fiscais
 * da PJ brasileira, configuração da NFS-e (produção e sandbox) e conferência
 * com a conta do Asaas.
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7: "quero poder editar no admin
 * `/config/sellerEntities/etiquetachef-br` todos os dados da empresa BR
 * necessários para cobrar e emitir nota", owner).
 *
 * Por quê: rota dedicada e não diálogo — são ~45 campos em seis blocos, e o
 * owner vai preencher com o contador ao lado. A spec da Sprint 35 previa
 * diálogo; ela foi atualizada junto (`docs/screens/billing-seller-entities.md`).
 *
 * Contrato: `/billing/seller-entities/new` cria; `/billing/seller-entities/:id`
 * edita (id imutável). Salvar valida com o MESMO schema do servidor
 * (`inputFromForm`), pede motivo e confirmação, e chama
 * `adminUpsertSellerEntity`. "Conferir" lê a conta do Asaas
 * (`adminGetAsaasFiscalInfo`, só leitura); "Enviar ao Asaas" ESCREVE o cadastro
 * fiscal na conta do modo escolhido (`adminSyncAsaasFiscalInfo`) — o
 * certificado A1 é enviado no painel do Asaas, nunca aqui.
 *
 * Usado por: rotas `billing/seller-entities/new` e `billing/seller-entities/:id`
 * (`routes/router.tsx`); `SellerEntitiesPage`.
 *
 * Arquitetura: a emissão da NFS-e lê `br_fiscal.nfse.{live|sandbox}` desta
 * entidade (`apps/functions/src/billing/asaas/nfse.ts`), e o `setMarketConfig`
 * recusa ASAAS live sem `nfse.live` preenchido.
 *
 * Falhas conhecidas:
 *  - 2026-10-07 (#756): serviço municipal (id/código/nome) e regimes eram texto
 *    livre; o owner digitou o NOME do serviço ("02800 | 1.05") no campo de
 *    código e a tela gravou — a nota iria com id e código juntos. Hoje são
 *    dropdowns com a lista da conta Asaas (`adminGetAsaasFiscalCatalog`), e o
 *    servidor confere de novo ao salvar. Não voltar a campo livre.
 *
 * Dicas: a "Tokenização aprovada" só deve ser ligada depois que o gerente de
 * conta do Asaas liberar em produção — ela libera o anual parcelado no live.
 * Teste: `SellerEntityEditorPage.test.tsx`.
 */
export function SellerEntityEditorPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const isNew = routeId === undefined || routeId === 'new';
  const [form, setForm] = useState<SellerEntityForm>(emptyForm());
  const [loading, setLoading] = useState(!isNew);
  const [notFound, setNotFound] = useState(false);
  const [reason, setReason] = useState('');
  // Erro que fica junto ao Salvar do rodapé (Form errorText) — o toast fica no
  // topo e não é visto por quem clicou no Salvar de baixo (#756, 2026-10-01 15:48 +0200).
  const [error, setErrorState] = useState<string | null>(null);
  // Erro por campo (chave do formulário ou `nfse_<modo>.<campo>`).
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fiscal, setFiscal] = useState<AsaasFiscalStatus | null>(null);
  const [fiscalBusy, setFiscalBusy] = useState(false);
  const [syncMode, setSyncMode] = useState<PaymentMode | null>(null);
  // Lista do Asaas por modo (dropdowns de serviço municipal e regimes, #756).
  // `services` é a última busca do operador; os regimes não mudam com ela.
  const [catalog, setCatalog] = useState<Partial<Record<PaymentMode, AsaasFiscalCatalog>>>({});
  const [catalogStatus, setCatalogStatus] = useState<Partial<Record<PaymentMode, 'loading' | 'error' | 'finished'>>>({});
  // Número da última busca por modo: resposta de busca anterior que chega
  // depois não sobrescreve a lista (auditoria #756, 2026-10-07 18:15 +0200).
  const catalogSeq = useRef<Partial<Record<PaymentMode, number>>>({});

  // Carrega a entidade uma vez (o listener devolve todas; o editor não
  // sobrescreve o que o usuário está digitando a cada snapshot).
  useEffect(() => {
    if (isNew) return undefined;
    let loaded = false;
    return subscribeSellerEntities((r) => {
      if (loaded || r.kind === 'LOADING') return;
      loaded = true;
      setLoading(false);
      const found = r.kind === 'READY' ? r.entities.find((e) => e.id === routeId) : undefined;
      if (found) setForm(formFromEntity(found));
      else setNotFound(true);
    });
  }, [isNew, routeId]);

  const isBr = form.headquarter_country === 'BR';
  const mapHref = mapsUrl(form);
  const cnaeOptions = [
    ...BR_ENTITY_CNAES.map((c) => ({
      value: c.value,
      label: `${c.code} — ${c.description}`,
      description: `LC 116 ${c.lc116} · ${t('billing.sellers.fields.cnaeAnnex', { annexes: c.simplesAnnexes.join(', ') })}`,
      ...(c.forInvoice ? { tags: [t('billing.sellers.fields.cnaeForInvoice')] } : {}),
    })),
    ...(form.cnae !== '' && !BR_ENTITY_CNAES.some((c) => c.value === form.cnae)
      ? [{ value: form.cnae, label: form.cnae }]
      : []),
  ];
  const capCurrency = (form.annual_cap_currency || form.default_currency) as Currency;
  // Teto anual ligado exige valor > 0 (o schema exige `positive`).
  const capInvalid =
    form.cap_mode === 'YEARLY_CAP' && (form.annual_cap_amount_cents === null || form.annual_cap_amount_cents <= 0);
  // PSP × modo da entidade ('' no modo = os dois, entidade anterior ao campo).
  const pspKey = form.payment_provider ? `${form.payment_provider}:${form.payment_mode}` : '';
  const pspOptions = [
    { value: '', label: t('billing.sellers.fields.paymentProviderNone') },
    ...MARKET_SELECTABLE_PROVIDERS.filter((p) => isBr || p !== 'asaas').flatMap((p) =>
      [PaymentMode.LIVE, PaymentMode.SANDBOX].map((m) => ({
        value: `${p}:${m}`,
        label: t(`billing.markets.pspChoice.${p}.${m}`),
      })),
    ),
    ...(form.payment_provider && !form.payment_mode
      ? [
          {
            value: `${form.payment_provider}:`,
            label: t('billing.sellers.fields.paymentProviderBothModes', {
              provider: t(`billing.markets.provider.${form.payment_provider}`),
            }),
          },
        ]
      : []),
  ];
  // Os modos que a tela mostra para a conta do PSP (NFS-e, conferir/enviar).
  const entityModes: PaymentMode[] =
    form.payment_mode === PaymentMode.LIVE
      ? [PaymentMode.LIVE]
      : form.payment_mode === PaymentMode.SANDBOX
        ? [PaymentMode.SANDBOX]
        : [PaymentMode.LIVE, PaymentMode.SANDBOX];

  /**
   * Mostra o erro no toast E junto ao Salvar do rodapé, a CADA chamada.
   * Bug corrigido em 2026-10-01 15:48 +0200 (#756): o toast saía de um `useEffect` em
   * `[error]`, então o mesmo erro no segundo clique não aparecia — "o Salvar
   * não faz nada". Não voltar para o efeito.
   */
  function setError(msg: string | null): void {
    setErrorState(msg);
    if (msg) toast.error(msg);
  }

  // Labels dos campos, preenchidos no render, para o resumo do erro. Os que
  // não passam por `text()` (selects, CNPJ, teto) entram aqui à mão.
  const labels: Record<string, string> = {
    cnae: 'CNAE',
    tax_id: form.headquarter_country === 'BR' ? 'CNPJ' : t('billing.sellers.fields.taxId'),
    annual_cap_amount_cents: t('billing.sellers.fields.capAmount'),
  };

  /** Tira o erro de um campo quando o operador o edita. */
  function clearFieldError(key: string): void {
    setFieldErrors((e) => {
      if (!(key in e)) return e;
      return Object.fromEntries(Object.entries(e).filter(([k]) => k !== key));
    });
  }

  /** Atualiza um campo do formulário e limpa o erro dele. */
  function set<K extends keyof SellerEntityForm>(key: K, value: SellerEntityForm[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
    clearFieldError(key);
  }
  /** Atualiza parcialmente o bloco NFS-e de um modo. */
  function setNfse(which: 'nfse_live' | 'nfse_sandbox', patch: Partial<NfseForm>): void {
    setForm((f) => ({ ...f, [which]: { ...f[which], ...patch } }));
  }

  /**
   * Busca na lista do Asaas do modo (serviços pelo texto + regimes). Erro vira
   * estado `error` do dropdown com "tentar de novo", nunca campo livre.
   */
  async function loadCatalog(mode: PaymentMode, description: string): Promise<void> {
    const seq = (catalogSeq.current[mode] ?? 0) + 1;
    catalogSeq.current[mode] = seq;
    setCatalogStatus((s) => ({ ...s, [mode]: 'loading' }));
    try {
      // O callable aceita até 100 caracteres (`AsaasMunicipalServicesInputSchema`).
      const got = await adminGetAsaasFiscalCatalogCallable({ mode, description: description.trim().slice(0, 100) });
      if (catalogSeq.current[mode] !== seq) return;
      setCatalog((c) => ({ ...c, [mode]: got }));
      setCatalogStatus((s) => ({ ...s, [mode]: 'finished' }));
    } catch {
      if (catalogSeq.current[mode] !== seq) return;
      setCatalogStatus((s) => ({ ...s, [mode]: 'error' }));
    }
  }

  // Os regimes vêm do município da conta: carrega a lista ao abrir qualquer
  // entidade BR (live, ou o sandbox quando ela é só sandbox). Auditoria #756
  // (2026-10-07 18:15 +0200): só carregava com PSP = Asaas, e uma PJ BR sem
  // PSP ficava com os regimes travados em "— Nenhum".
  // Mesma regra do servidor (`assertFiscalCatalog`): sandbox só quando a
  // entidade é só sandbox.
  const regimeMode = form.payment_mode === PaymentMode.SANDBOX ? PaymentMode.SANDBOX : PaymentMode.LIVE;
  const wantsCatalog = !loading && isBr;
  useEffect(() => {
    if (wantsCatalog && catalog[regimeMode] === undefined && catalogStatus[regimeMode] === undefined) {
      void loadCatalog(regimeMode, '');
    }
  });

  /** Campo de texto ligado ao formulário; registra o rótulo para o resumo de erros. */
  function text(key: FieldKey, label: string, opts: { description?: string; disabled?: boolean } = {}): React.JSX.Element {
    labels[key] = label;
    return (
      <FormField label={label} description={opts.description} errorText={fieldErrors[key]}>
        <Input
          value={form[key]}
          invalid={fieldErrors[key] !== undefined}
          disabled={opts.disabled === true || submitting}
          onChange={({ detail }): void => {
            set(key, detail.value);
          }}
        />
      </FormField>
    );
  }

  /** Valida (motivo, CNPJ, teto, schema) e abre a confirmação, ou marca cada campo errado. */
  function requestSave(): void {
    if (reason.trim() === '') {
      setError(t('billing.sellers.errors.reasonRequired'));
      return;
    }
    // CNPJ inválido (DV) bloqueia antes do schema, que só confere o tamanho.
    if (taxIdError(form) !== null || capInvalid) {
      setShowErrors(true);
      setError(t('billing.sellers.errors.fixFields'));
      return;
    }
    const parsed = inputFromForm(form, reason);
    if (!parsed.success) {
      // TODOS os problemas, cada um no seu campo, em português (#756).
      const next: Record<string, string> = {};
      for (const i of fieldIssues(parsed.error.issues)) {
        if (next[i.field] !== undefined) continue;
        // Campo VAZIO com regex (IBGE, NBS) acusa formato no Zod; para quem
        // preenche, é "obrigatório".
        const [blk, sub] = i.field.split('.');
        const value: unknown =
          sub !== undefined && (blk === 'nfse_live' || blk === 'nfse_sandbox')
            ? (form[blk] as Record<string, unknown>)[sub]
            : (form as Record<string, unknown>)[i.field];
        const kind = i.kind === 'format' && value === '' ? 'required' : i.kind;
        next[i.field] = kind === 'custom' ? i.message : t(`billing.sellers.errors.field.${kind}`);
      }
      setFieldErrors(next);
      const names = Object.keys(next).map((k) => labels[k] ?? k);
      setError(t('billing.sellers.errors.fieldsList', { fields: names.join(', ') }));
      // Leva o operador ao primeiro campo com problema (o formulário é longo).
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.focus({ preventScroll: true });
      });
      return;
    }
    setFieldErrors({});
    setError(null);
    setConfirmOpen(true);
  }

  /** Grava pelo `adminUpsertSellerEntity` e volta para a lista. */
  async function save(): Promise<void> {
    const parsed = inputFromForm(form, reason);
    if (!parsed.success) return;
    setSubmitting(true);
    try {
      await adminUpsertSellerEntityCallable(parsed.data);
      toast.success(t('billing.sellers.saved'));
      setConfirmOpen(false);
      void navigate('/billing/seller-entities');
    } catch (err) {
      setConfirmOpen(false);
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  /** Lê o cadastro fiscal da conta Asaas do modo (só leitura). */
  async function checkAsaas(mode: PaymentMode): Promise<void> {
    setFiscalBusy(true);
    try {
      setFiscal(await adminGetAsaasFiscalInfoCallable({ mode, sellerEntityId: form.id }));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setFiscalBusy(false);
    }
  }

  /** Escreve o cadastro fiscal na conta Asaas do modo; exige motivo. */
  async function syncAsaas(mode: PaymentMode): Promise<void> {
    // O envio escreve na conta do Asaas: o motivo é obrigatório como no salvar
    // (auditoria #756 — antes um motivo fixo era inventado quando vazio).
    if (reason.trim() === '') {
      setSyncMode(null);
      setError(t('billing.sellers.errors.reasonRequired'));
      return;
    }
    setFiscalBusy(true);
    try {
      setFiscal(await adminSyncAsaasFiscalInfoCallable({ mode, reason: reason.trim(), sellerEntityId: form.id }));
      toast.success(t('billing.sellers.asaas.synced'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setFiscalBusy(false);
      setSyncMode(null);
    }
  }

  if (loading) {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }
  if (notFound) return <Alert type="error">{t('billing.sellers.notFound')}</Alert>;

  /** Opções de select a partir de valores, traduzidas por `prefix` quando dado. */
  const options = (values: readonly string[], prefix?: string): { value: string; label: string }[] =>
    values.map((v) => ({ value: v, label: prefix ? t(`${prefix}.${v}`) : v }));

  /**
   * Serviço municipal: dropdown com busca na lista da conta Asaas do modo. Id e
   * nome saem juntos do item escolhido — não há campo livre (#756, 2026-10-07).
   * O valor gravado continua visível mesmo antes da busca carregar.
   */
  function serviceSelect(which: 'nfse_live' | 'nfse_sandbox', mode: PaymentMode, title: string): React.JSX.Element {
    const n = form[which];
    const id = `${which}.municipal_service_id`;
    const label = t('billing.sellers.nfse.municipalService');
    const nameId = `${which}.municipal_service_name`;
    labels[id] = `${title} · ${label}`;
    labels[nameId] = `${title} · ${label}`;
    const current = n.municipal_service_id
      ? { value: n.municipal_service_id, label: n.municipal_service_name, tags: [`id ${n.municipal_service_id}`] }
      : null;
    const options = (catalog[mode]?.services ?? []).map((s) => ({
      value: s.id,
      label: s.name,
      tags: [`id ${s.id}`],
    }));
    return (
      <FormField
        label={label}
        description={t('billing.sellers.nfse.municipalServiceHint')}
        errorText={fieldErrors[id] ?? fieldErrors[nameId]}
      >
        <Select
          selectedOption={current}
          options={current && !options.some((o) => o.value === current.value) ? [current, ...options] : options}
          filteringType="manual"
          filteringPlaceholder={t('billing.sellers.nfse.municipalServiceSearch')}
          placeholder={t('billing.sellers.nfse.municipalServicePlaceholder')}
          statusType={catalogStatus[mode] ?? 'pending'}
          loadingText={t('billing.sellers.nfse.catalogLoading')}
          errorText={t('billing.sellers.nfse.catalogError')}
          recoveryText={t('billing.sellers.nfse.catalogRetry')}
          empty={t('billing.sellers.nfse.catalogEmpty')}
          invalid={fieldErrors[id] !== undefined || fieldErrors[nameId] !== undefined}
          disabled={submitting}
          onLoadItems={({ detail }): void => {
            void loadCatalog(mode, detail.filteringText);
          }}
          onChange={({ detail }): void => {
            const picked = catalog[mode]?.services.find((s) => s.id === detail.selectedOption.value);
            if (!picked) return;
            setNfse(which, { municipal_service_id: picked.id, municipal_service_name: picked.name });
            clearFieldError(id);
            clearFieldError(nameId);
          }}
        />
      </FormField>
    );
  }

  /**
   * Regime especial / do Portal Nacional: dropdown com a lista do município da
   * conta Asaas (eram texto livre até 2026-10-07, #756). Valor gravado fora da
   * lista aparece marcado, para não sumir em silêncio.
   */
  function regimeSelect(
    key: 'special_tax_regime' | 'national_portal_tax_calculation_regime',
    label: string,
    list: 'specialTaxRegimes' | 'nationalPortalRegimes',
  ): React.JSX.Element {
    labels[key] = label;
    const items = catalog[regimeMode]?.[list] ?? [];
    const options = [
      { value: '', label: t('billing.sellers.fields.regimeNone') },
      ...items.map((o) => ({ value: o.value, label: `${o.value} — ${o.label}` })),
    ];
    const value = form[key];
    const known = options.find((o) => o.value === value);
    // "Fora da lista" só depois de a lista CHEGAR — carregando ou com erro, o
    // valor gravado aparece como está, sem acusar (auditoria #756).
    const listed = catalogStatus[regimeMode] === 'finished';
    const selected = known ?? { value, label: listed ? t('billing.sellers.fields.outOfList', { value }) : value };
    return (
      <FormField label={label} errorText={fieldErrors[key]}>
        <Select
          selectedOption={selected}
          options={known ? options : [...options, selected]}
          statusType={catalogStatus[regimeMode] ?? 'pending'}
          loadingText={t('billing.sellers.nfse.catalogLoading')}
          errorText={t('billing.sellers.nfse.catalogError')}
          recoveryText={t('billing.sellers.nfse.catalogRetry')}
          invalid={fieldErrors[key] !== undefined || (listed && !known)}
          disabled={submitting}
          onLoadItems={(): void => {
            if (catalogStatus[regimeMode] === 'error') void loadCatalog(regimeMode, '');
          }}
          onChange={({ detail }): void => {
            set(key, detail.selectedOption.value ?? '');
          }}
        />
      </FormField>
    );
  }

  /** Bloco de configuração da NFS-e de um modo (serviço, alíquotas, observações). */
  function nfseBlock(which: 'nfse_live' | 'nfse_sandbox', title: string): React.JSX.Element {
    const n = form[which];
    const mode = which === 'nfse_live' ? PaymentMode.LIVE : PaymentMode.SANDBOX;
    const field = (
      key: Exclude<keyof NfseForm, 'enabled' | 'retainIss' | 'municipal_service_id' | 'municipal_service_name'>,
      label: string,
      opts: { decimal?: boolean } = {},
    ): React.JSX.Element => {
      const id = `${which}.${key}`;
      labels[id] = `${title} · ${label}`;
      return (
        <FormField label={label} errorText={fieldErrors[id]}>
          <Input
            value={n[key]}
            // Alíquota: teclado numérico com vírgula; `num` aceita "2,9" e
            // "2.9" (owner, 2026-10-07, #756).
            {...(opts.decimal === true ? { inputMode: 'decimal' as const, placeholder: '0,00' } : {})}
            disabled={submitting}
            invalid={fieldErrors[id] !== undefined}
            onChange={({ detail }): void => {
              setNfse(which, { [key]: detail.value });
              clearFieldError(id);
            }}
          />
        </FormField>
      );
    };
    return (
      <Container header={<Header variant="h2" description={t('billing.sellers.nfse.description')}>{title}</Header>}>
        <SpaceBetween size="m">
          <Toggle
            checked={n.enabled}
            onChange={({ detail }): void => {
              setNfse(which, { enabled: detail.checked });
            }}
          >
            {t('billing.sellers.nfse.enabled')}
          </Toggle>
          {n.enabled && (
            <>
              <ColumnLayout columns={2}>
                {field('service_description', t('billing.sellers.nfse.serviceDescription'))}
                {field('observations', t('billing.sellers.nfse.observations'))}
              </ColumnLayout>
              {serviceSelect(which, mode, title)}
              <Box variant="small" color="text-body-secondary">
                {t('billing.sellers.nfse.percentHint')}
              </Box>
              <ColumnLayout columns={3}>
                  {field('iss', 'ISS (%)', { decimal: true })}
                  {field('pis', 'PIS (%)', { decimal: true })}
                  {field('cofins', 'COFINS (%)', { decimal: true })}
                  {field('csll', 'CSLL (%)', { decimal: true })}
                  {field('inss', 'INSS (%)', { decimal: true })}
                  {field('ir', 'IR (%)', { decimal: true })}
              </ColumnLayout>
              <Toggle
                checked={n.retainIss}
                onChange={({ detail }): void => {
                  setNfse(which, { retainIss: detail.checked });
                }}
              >
                {t('billing.sellers.nfse.retainIss')}
              </Toggle>
            </>
          )}
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('billing.sellers.editorSubtitle')}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                iconName="arrow-left"
                onClick={(): void => {
                  void navigate('/billing/seller-entities');
                }}
              >
                {t('billing.sellers.back')}
              </Button>
              {/* Salvar também no topo (owner, 2026-10-01) — o formulário é longo. */}
              <Button variant="primary" loading={submitting} onClick={requestSave}>
                {t('billing.sellers.save')}
              </Button>
            </SpaceBetween>
          }
        >
          {isNew ? t('billing.sellers.createTitle') : t('billing.sellers.editTitle', { id: form.id })}
        </Header>
      }
    >
      <Form
        errorText={error ?? undefined}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              disabled={submitting}
              onClick={(): void => {
                void navigate('/billing/seller-entities');
              }}
            >
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button variant="primary" loading={submitting} onClick={requestSave}>
              {t('billing.sellers.save')}
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">{t('billing.sellers.sections.company')}</Header>}>
            <SpaceBetween size="m">
              <ColumnLayout columns={2}>
                {text('id', t('billing.sellers.fields.id'), {
                  description: t('billing.sellers.fields.idHint'),
                  disabled: !isNew,
                })}
                <FormField label={t('billing.sellers.fields.status')}>
                  <Select
                    selectedOption={{ value: form.status, label: t(`billing.sellers.status.${form.status}`) }}
                    options={options(ALL_SELLER_ENTITY_STATUSES, 'billing.sellers.status')}
                    onChange={({ detail }): void => {
                      set('status', detail.selectedOption.value as SellerEntityForm['status']);
                    }}
                  />
                </FormField>
                {text('legal_name', t('billing.sellers.fields.legalName'))}
                {text('trading_name', t('billing.sellers.fields.tradingName'))}
                {text('headquarter_country', t('billing.sellers.fields.country'), { disabled: !isNew })}
                <FormField
                  label={isBr ? 'CNPJ' : t('billing.sellers.fields.taxId')}
                  errorText={(showErrors ? taxIdError(form) : null) ?? fieldErrors.tax_id}
                >
                  {/* Máscara de CNPJ quando a sede é BR (owner, 2026-10-01, #756). */}
                  <Input
                    value={form.tax_id}
                    inputMode={isBr ? 'numeric' : 'text'}
                    {...(isBr ? { placeholder: '00.000.000/0000-00' } : {})}
                    disabled={submitting}
                    invalid={(showErrors && taxIdError(form) !== null) || fieldErrors.tax_id !== undefined}
                    onChange={({ detail }): void => {
                      set('tax_id', formatTaxIdInput(form.headquarter_country, detail.value));
                    }}
                  />
                </FormField>
                <FormField
                  label={t('billing.sellers.fields.paymentProvider')}
                  description={t('billing.sellers.fields.paymentProviderHint')}
                >
                  {/* #756 (owner, 2026-10-01): PSP × MODO, como no país — "cadê
                      as SANDBOX?". O Asaas só para PJ com sede no BR. Entidade
                      antiga sem modo aparece como "produção e sandbox". */}
                  <Select
                    selectedOption={pspOptions.find((o) => o.value === pspKey) ?? null}
                    options={pspOptions}
                    placeholder={t('billing.sellers.fields.paymentProviderPlaceholder')}
                    onChange={({ detail }): void => {
                      const [p = '', m = ''] = (detail.selectedOption.value ?? '').split(':');
                      setForm((f) => ({ ...f, payment_provider: p, payment_mode: m }));
                    }}
                  />
                </FormField>
                <FormField label={t('billing.sellers.fields.currency')}>
                  <Select
                    selectedOption={{ value: form.default_currency, label: form.default_currency }}
                    options={options(ALL_CURRENCIES)}
                    onChange={({ detail }): void => {
                      set('default_currency', detail.selectedOption.value ?? 'BRL');
                    }}
                  />
                </FormField>
                <FormField label={t('billing.sellers.fields.capMode')}>
                  <RadioGroup
                    value={form.cap_mode}
                    onChange={({ detail }): void => {
                      set('cap_mode', detail.value as SellerEntityForm['cap_mode']);
                    }}
                    items={[
                      { value: 'UNLIMITED', label: t('billing.sellers.fields.capUnlimited') },
                      { value: 'YEARLY_CAP', label: t('billing.sellers.fields.capYearly') },
                    ]}
                  />
                </FormField>
                {form.cap_mode === 'YEARLY_CAP' && (
                  // Campo de moeda de verdade (owner, 2026-10-01, #756): só
                  // dígitos, centavos da direita, na moeda do teto — sem o parse
                  // ambíguo de "100.000" que o texto livre exigia.
                  <FormField
                    label={t('billing.sellers.fields.capAmount')}
                    errorText={capInvalid && showErrors ? t('billing.sellers.errors.capRequired') : undefined}
                  >
                    <CurrencyInput
                      cents={form.annual_cap_amount_cents}
                      currency={capCurrency}
                      disabled={submitting}
                      invalid={capInvalid && showErrors}
                      ariaLabel={t('billing.sellers.fields.capAmount')}
                      onChange={(c): void => {
                        set('annual_cap_amount_cents', c);
                      }}
                    />
                  </FormField>
                )}
                {text('notes', t('billing.sellers.fields.notes'))}
              </ColumnLayout>
            </SpaceBetween>
          </Container>

          <Container
            header={
              <Header
                variant="h2"
                actions={
                  // Conferir o endereço no mapa (owner, 2026-10-01, #756).
                  mapHref !== null ? (
                    <Link external href={mapHref}>
                      {t('billing.sellers.fields.openInMaps')}
                    </Link>
                  ) : undefined
                }
              >
                {t('billing.sellers.sections.address')}
              </Header>
            }
          >
            <ColumnLayout columns={2}>
              {text('street', t('billing.sellers.fields.street'))}
              {isBr && text('address_number', t('billing.sellers.fields.addressNumber'))}
              {isBr && text('address_complement', t('billing.sellers.fields.addressComplement'))}
              {isBr && text('district', t('billing.sellers.fields.district'))}
              {text('city', t('billing.sellers.fields.city'))}
              {text('state', t('billing.sellers.fields.state'))}
              {text('postal_code', t('billing.sellers.fields.postalCode'))}
              {isBr && text('ibge_city_code', t('billing.sellers.fields.ibgeCityCode'))}
            </ColumnLayout>
          </Container>

          {isBr && (
            <Container
              header={
                <Header variant="h2" description={t('billing.sellers.sections.fiscalBrDescription')}>
                  {t('billing.sellers.sections.fiscalBr')}
                </Header>
              }
            >
              <SpaceBetween size="m">
                <ColumnLayout columns={2}>
                  {text('municipal_registration', t('billing.sellers.fields.municipalRegistration'))}
                  {text('state_registration', t('billing.sellers.fields.stateRegistration'))}
                  <FormField label="CNAE" description={t('billing.sellers.fields.cnaeHint')} errorText={fieldErrors.cnae}>
                    {/* Dropdown com os CNAEs da PJ (owner, 2026-10-01, #756);
                        6203-1/00 é o da nota. Valor fora da lista (doc antigo)
                        continua visível para não sumir em silêncio. */}
                    <Select
                      selectedOption={cnaeOptions.find((o) => o.value === form.cnae) ?? null}
                      options={cnaeOptions}
                      placeholder={t('billing.sellers.fields.cnaePlaceholder')}
                      invalid={fieldErrors.cnae !== undefined}
                      disabled={submitting}
                      onChange={({ detail }): void => {
                        set('cnae', detail.selectedOption.value ?? '');
                      }}
                    />
                  </FormField>
                  <FormField label={t('billing.sellers.fields.taxRegime')}>
                    <Select
                      selectedOption={{ value: form.tax_regime, label: t(`billing.sellers.taxRegime.${form.tax_regime}`) }}
                      options={options(ALL_BR_TAX_REGIMES, 'billing.sellers.taxRegime')}
                      onChange={({ detail }): void => {
                        set('tax_regime', detail.selectedOption.value as SellerEntityForm['tax_regime']);
                      }}
                    />
                  </FormField>
                  {regimeSelect('special_tax_regime', t('billing.sellers.fields.specialTaxRegime'), 'specialTaxRegimes')}
                  {regimeSelect(
                    'national_portal_tax_calculation_regime',
                    t('billing.sellers.fields.nationalPortalRegime'),
                    'nationalPortalRegimes',
                  )}
                  {text('nbs_code', 'NBS', { description: t('billing.sellers.fields.nbsHint') })}
                  {text('fiscal_email', t('billing.sellers.fields.fiscalEmail'))}
                  {text('rps_serie', t('billing.sellers.fields.rpsSerie'))}
                  {text('rps_number', t('billing.sellers.fields.rpsNumber'))}
                  {text('lote_number', t('billing.sellers.fields.loteNumber'))}
                </ColumnLayout>
              </SpaceBetween>
            </Container>
          )}

          {/* Owner, 2026-10-01: tudo o que é de PSP segue o "PSP desta
              entidade". NFS-e é emitida pelo Asaas — com Stripe a nota é a
              invoice do próprio Stripe, então os blocos de NFS-e e a conta do
              Asaas só aparecem com o Asaas. Dados da CONTA do PSP (ex.: Stripe
              Account ID) ficam na tela do PSP, `/psp/:id` — não aqui. */}
          {/* NFS-e conforme o PSP × modo escolhido (owner, 2026-10-01). */}
          {/* Produção e sandbox lado a lado (owner, 2026-10-07, #756): os
              mesmos campos, comparáveis num olhar. */}
          {form.payment_provider === 'asaas' && (
            <ColumnLayout columns={entityModes.length}>
              {entityModes.includes(PaymentMode.LIVE) && nfseBlock('nfse_live', t('billing.sellers.sections.nfseLive'))}
              {entityModes.includes(PaymentMode.SANDBOX) &&
                nfseBlock('nfse_sandbox', t('billing.sellers.sections.nfseSandbox'))}
            </ColumnLayout>
          )}

          {form.payment_provider === 'asaas' && (
            <Container
              header={
                <Header variant="h2" description={t('billing.sellers.asaas.description')}>
                  {t('billing.sellers.sections.asaas')}
                </Header>
              }
            >
              <SpaceBetween size="m">
                {entityModes.includes(PaymentMode.LIVE) && (
                <Toggle
                  checked={form.asaas_live_tokenization_enabled}
                  onChange={({ detail }): void => {
                    set('asaas_live_tokenization_enabled', detail.checked);
                  }}
                  description={t('billing.sellers.fields.tokenizationHint')}
                >
                  {t('billing.sellers.fields.tokenization')}
                </Toggle>
                )}
                {!isNew && (
                <SpaceBetween direction="horizontal" size="xs">
                  {entityModes.map((mode) => (
                    <Button key={`check-${mode}`} loading={fiscalBusy} onClick={(): void => void checkAsaas(mode)}>
                      {t(`billing.sellers.asaas.check.${mode}`)}
                    </Button>
                  ))}
                  {entityModes.map((mode) => (
                    <Button
                      key={`sync-${mode}`}
                      disabled={fiscalBusy}
                      onClick={(): void => {
                        setSyncMode(mode);
                      }}
                    >
                      {t(`billing.sellers.asaas.sync.${mode}`)}
                    </Button>
                  ))}
                </SpaceBetween>
                )}
                {fiscal && (
                  <SpaceBetween size="s">
                    {!fiscal.entityHasFiscal && <Alert type="info">{t('billing.sellers.asaas.noFiscal')}</Alert>}
                    <Alert
                      type={
                        fiscal.entityHasFiscal && fiscal.configured && fiscal.certificateSent && fiscal.diffs.length === 0
                          ? 'success'
                          : 'warning'
                      }
                    >
                      {t('billing.sellers.asaas.summary', {
                        mode: t(`billing.sellers.asaas.mode.${fiscal.mode}`),
                        configured: fiscal.configured ? t('billing.sellers.asaas.yes') : t('billing.sellers.asaas.no'),
                        certificate: fiscal.certificateSent ? t('billing.sellers.asaas.yes') : t('billing.sellers.asaas.no'),
                        diffs: fiscal.diffs.length,
                      })}
                    </Alert>
                    {fiscal.diffs.length > 0 && (
                      <Table
                        items={fiscal.diffs}
                        trackBy="field"
                        variant="embedded"
                        columnDefinitions={[
                          { id: 'field', header: t('billing.sellers.asaas.field'), cell: (d): string => d.field },
                          { id: 'entity', header: t('billing.sellers.asaas.entity'), cell: (d): string => d.entity ?? '—' },
                          { id: 'account', header: t('billing.sellers.asaas.account'), cell: (d): string => d.account ?? '—' },
                        ]}
                      />
                    )}
                  </SpaceBetween>
                )}
              </SpaceBetween>
            </Container>
          )}

          <Container>
            <FormField label={t('billing.sellers.fields.reason')} description={t('billing.sellers.fields.reasonHint')}>
              <Input
                value={reason}
                onChange={({ detail }): void => {
                  setReason(detail.value);
                }}
              />
            </FormField>
          </Container>
        </SpaceBetween>
      </Form>

      <AdminConfirm
        visible={confirmOpen}
        title={t('billing.sellers.confirmTitle')}
        confirmLabel={t('billing.sellers.save')}
        cancelLabel={t('tenantDetail.actions.cancel')}
        loading={submitting}
        onConfirm={(): void => {
          void save();
        }}
        onDismiss={(): void => {
          setConfirmOpen(false);
        }}
      >
        <Box variant="p">{t('billing.sellers.confirmDescription', { id: form.id })}</Box>
      </AdminConfirm>

      <AdminConfirm
        visible={syncMode !== null}
        title={t('billing.sellers.asaas.syncConfirmTitle')}
        confirmLabel={t('billing.sellers.asaas.syncConfirm')}
        cancelLabel={t('tenantDetail.actions.cancel')}
        loading={fiscalBusy}
        onConfirm={(): void => {
          if (syncMode) void syncAsaas(syncMode);
        }}
        onDismiss={(): void => {
          setSyncMode(null);
        }}
      >
        <Box variant="p">
          {t('billing.sellers.asaas.syncConfirmDescription', {
            mode: syncMode ? t(`billing.sellers.asaas.mode.${syncMode}`) : '',
          })}
        </Box>
      </AdminConfirm>
    </ContentLayout>
  );
}
